<script lang="ts" setup>
import PageFooter from '@/components/footer/PageFooter.vue'
import PageHeader from '@/components/header/PageHeader.vue'
import BrandMapping from '@/helper/BrandMapping'
import { ButtonSetProps } from '@/helper/Types'
import ButtonSet from '@/components/ButtonSet.vue'
import { api } from '@/services/ApiService'
import appStore from '@/store/AppStore'
import userStore from '@/store/UserStore'
import FileChooser from '@/components/admin/FileChooser.vue'
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { setMetaTitle } from '@/helper/tools'

const router = useRouter()

const selectedBrand = ref('')
const selectedCountry = ref('')
const productImportStatus = ref('')
const systemSchemesImportStatus = ref('')
const productCatalogDumpInProgress = ref(false)
const configs: Record<string, Record<string, string[]>> = reactive({})

const brandData: Record<string, { caption: string; logoPath: string }> = {
  VAILLANT: {
    caption: 'Vaillant',
    logoPath: `/static/img/logo-${BrandMapping.getLogo('VAILLANT')}.svg`
  },
  SDBG: {
    caption: 'Saunier Duval',
    logoPath: `/static/img/logo-${BrandMapping.getLogo('SAUNIERDUVAL')}.svg`
  }
}

onMounted(async () => {
  if (!userStore.isPrivileged) {
    router.push({ name: 'ProjectOverviewPage' }).catch(() => '')
  } else {
    appStore.isLoading = true
    try {
      const configsRes = await api.loadAvailableAdminConfigs()
      const translationsRes = await api.loadAvailableAdminTranslations()

      translationsRes.sort((a, b) => (a.brand > b.brand ? -1 : 1))
      translationsRes.forEach(item => {
        const brand = (configs[item.brand] = configs[item.brand] ? configs[item.brand] : {})
        const [language, country] = item.locale.toUpperCase().split('_')
        brand[country] = brand[country] ? brand[country] : []
        brand[country].push(language)
      })

      configsRes.forEach(item => {
        if (!configs[item.brand]) {
          configs[item.brand] = {}
        }

        if (!configs[item.brand][item.country]) {
          configs[item.brand][item.country] = []
        }
      })
    } catch (e) {
      router.push({ name: 'ProjectOverviewPage' }).catch(() => '')
      return
    } finally {
      appStore.isLoading = false
    }
    setMetaTitle(`Admin config`)
  }
})

const brandButtons = computed((): ButtonSetProps[] => {
  return Object.keys(configs || []).map(
    brand =>
      ({
        caption: brandData[brand].caption,
        value: brand,
        iconUrl: brandData[brand].logoPath
      } as ButtonSetProps)
  )
})

const countryButtons = computed((): ButtonSetProps[] => {
  return Object.keys(configs[selectedBrand.value] || [])
    .sort()
    .map(
      country =>
        ({
          caption: country.toUpperCase(),
          value: country,
          iconUrl: `/static/img/flag-${country.toLowerCase()}.svg`
        } as ButtonSetProps)
    )
})

const transConfigButtons = computed((): ButtonSetProps[] => {
  if (!configs[selectedBrand.value]?.[selectedCountry.value]) {
    resetSelectedCountry()
    return []
  }

  return [
    ...configs[selectedBrand.value][selectedCountry.value].map(
      lang =>
        ({
          caption: `Edit ${lang.toUpperCase()} translation`,
          value: lang
        } as ButtonSetProps)
    ),
    {
      caption: 'Edit configuration',
      value: ''
    } as ButtonSetProps,
    {
      caption: `Edit news`,
      value: 'news'
    }
  ]
})

function resetSelectedCountry() {
  selectedCountry.value = ''
}

async function exportOutdoorTemperatureClicked() {
  await api.downloadOutdoorTemperature(selectedCountry.value)
}

async function downloadProductCatalogDump() {
  productCatalogDumpInProgress.value = true
  await api.downloadProductCatalogDump()
  productCatalogDumpInProgress.value = false
}

async function importProducts(reimportPictures: boolean) {
  try {
    productImportStatus.value = 'Product import started'
    await api.importProducts(reimportPictures)
    productImportStatus.value = 'Product import completed'
  } catch (e: Error) {
    productImportStatus.value = e.message
  }
}

async function importSystemSchemes(forceReimport: boolean) {
  try {
    systemSchemesImportStatus.value = 'System schemes import started'
    await api.importSystemSchemes(forceReimport)
    systemSchemesImportStatus.value = 'System schemes import completed'
  } catch (e: Error) {
    systemSchemesImportStatus.value = e.message
  }
}

function transConfigClicked(language: string) {
  if (language === '') {
    router.push({
      name: 'ConfigEditPage',
      params: {
        brand: selectedBrand.value.toString(),
        countryCode: selectedCountry.value.toString()
      }
    })
  } else if (language === 'news') {
    router.push({
      name: 'NewsEditPage',
      params: {
        brand: selectedBrand.value.toString(),
        countryCode: selectedCountry.value.toString()
      }
    })
  } else if (language !== '') {
    router.push({
      name: 'TranslationEditPage',
      params: {
        brand: selectedBrand.value.toString(),
        locale: `${language.toLowerCase()}_${selectedCountry.value.toString()}`
      }
    })
  }
}

async function uploadOutdoorTemperatures(files: FileList) {
  appStore.isLoading = true
  const response = await api.updateOutdoorTemperature(files[0])
  appStore.isLoading = false
  if (response.ok) {
    alert('Outdoor temperatures updated')
  } else if (response.status === 400) {
    const body = await response.json()
    alert(`Validation failed: \n\n${body?.join('\n\n')}`)
  } else {
    alert('Updating Outdoor temperatures failed')
  }
}
</script>

<template>
  <div>
    <PageHeader />

    <div class="main-wrapper">
      <div class="container main">
        <div class="form card">
          <div class="form__section">
            <div class="form__section-inner">
              <h2 class="form__title">
                Select the data to edit
              </h2>
              <div class="form__section has--line">
                <div class="form__section-inner">
                  <div class="form__title">
                    <h2>Brand</h2>
                  </div>
                  <ButtonSet
                    key="button-set-brand-selection"
                    v-model="selectedBrand"
                    :buttons="brandButtons"
                    :fill-width="true"
                    :is-unselectable="true"
                    :large="true"
                    name="brand"
                  />
                </div>
              </div>

              <div v-if="selectedBrand" class="form__section has--line has--brands">
                <div class="form__section-inner">
                  <div class="form__title">
                    <h2>Country</h2>
                  </div>
                  <ButtonSet
                    key="button-set-select-country"
                    v-model="selectedCountry"
                    :auto-btn-width="true"
                    :buttons="countryButtons"
                    :fill-width="false"
                    :has-large-space="true"
                    :large="false"
                    name="country"
                  />
                </div>
              </div>

              <div v-if="selectedCountry" class="form__section has--line">
                <div class="form__section-inner">
                  <div class="form__title">
                    <h2>Edit data</h2>
                  </div>
                  <ButtonSet
                    key="button-set-select-trans-config"
                    :auto-btn-width="true"
                    :buttons="transConfigButtons"
                    :fill-width="false"
                    :has-large-space="true"
                    :large="false"
                    name="transConfig"
                    @update:model-value="transConfigClicked"
                  />
                </div>
              </div>
              <div v-if="selectedCountry" class="form__section has--line">
                <div class="form__section-inner">
                  <div class="form__title">
                    <h2>Outdoor temperatures</h2>
                  </div>
                  <button class="btn is--secondary" @click="exportOutdoorTemperatureClicked">
                    {{ `Export ${selectedCountry.toUpperCase()} outdoor temperatures` }}
                  </button>
                </div>
              </div>

              <div class="form__section has--line">
                <div class="form__section-inner">
                  <h2 class="form__title">
                    Upload outdoor temperature mapping
                  </h2>
                  <file-chooser @selected="uploadOutdoorTemperatures" />
                </div>
              </div>

              <div class="form__section has--line">
                <div class="form__section-inner">
                  <h2 class="form__title">
                    Tools
                  </h2>
                  <div class="button-wrapper inline">
                    <div>
                      <button class="btn is--secondary" @click="importProducts(false)">
                        Start product import
                      </button>
                    </div>
                    <div>{{ productImportStatus }}</div>
                  </div>
                  <div class="button-wrapper inline">
                    <div>
                      <button class="btn is--secondary" @click="importProducts(true)">
                        Start product import without cache
                      </button>
                    </div>
                  </div>
                  <div class="button-wrapper inline">
                    <div>
                      <button class="btn is--secondary" @click="importSystemSchemes(true)">
                        Start system schemes import
                      </button>
                    </div>
                    <div>{{ systemSchemesImportStatus }}</div>
                  </div>
                  <div class="button-wrapper inline">
                    <div>
                      <button class="btn is--secondary" @click="importSystemSchemes(true)">
                        Start system schemes import without cache
                      </button>
                    </div>
                    <div>{{ systemSchemesImportStatus }}</div>
                  </div>
                  <div class="button-wrapper">
                    <button
                      :disabled="productCatalogDumpInProgress"
                      class="btn is--secondary"
                      @click="downloadProductCatalogDump"
                    >
                      PIM product catalog dump
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <PageFooter />
  </div>
</template>

<style lang="scss">
.has--brands {
  img.select-btn__img {
    width: rem-calc(36);
    height: rem-calc(24);
    object-fit: cover;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
  }

  .select-btn {
    max-width: rem-calc(120);
    min-width: rem-calc(120);
  }
}

.button-wrapper {
  margin: rem-calc(8);

  &.inline {
    display: flex;
    flex-direction: row;
    align-items: baseline;

    > * {
      margin-right: rem-calc(8);
    }
  }
}
</style>
